import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, signInAnonymously } from 'firebase/auth';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Set persistence to LOCAL to avoid sessionStorage issues in some iframe/cross-site contexts
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.error("Could not set auth persistence:", err);
});

export async function ensureFirebaseAuth() {
  if (!auth.currentUser) {
    try {
      await signInAnonymously(auth);
    } catch (err) {
      console.error("Failed to sign in anonymously:", err);
    }
  }
  return auth.currentUser;
}

export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  
  // Attempt to write to system logs, but catch any error to prevent infinite loops
  try {
    const errorLog = {
      type: 'firestore',
      message: errInfo.error,
      details: JSON.stringify({ operationType, path, authInfo: errInfo.authInfo }),
      path: path || 'unknown',
      userId: auth.currentUser?.uid || 'anonymous',
      userEmail: auth.currentUser?.email || 'none',
      timestamp: new Date().toISOString(),
      resolved: false
    };
    addDoc(collection(db, 'system_errors'), errorLog).catch(() => {
      // Silently ignore if client lacks write permissions to system_errors collection
    });
  } catch (e) {
    // Ignore synchronous errors
  }
}
